import React from "react";
import PropTypes from "prop-types";
import {PieChart, Pie, ResponsiveContainer, Tooltip, Cell} from "recharts";
import {Card} from "react-bootstrap";
import {ExportToExcel} from "../ExportToExcel.jsx";
import {CopyDataToClipboard} from "../CopyDataToClipboard.jsx";
import {useSelector} from "react-redux";
import TooltipInfo from "../TooltipInfo.jsx";

const RADIAN = Math.PI / 180;

// jedna uniwersalna funkcja do obu kół
function computePosition({ cx, cy, midAngle, radius, offset = 10, extraOffset = 0 }) {
    const rad = -midAngle * RADIAN;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    // początek linii (na obwodzie kola), cx/y - wsp srodka wykresu, sx/y - wsp startowe linii
    const sx = cx + radius * cos;
    const sy = cy + radius * sin;
    // koniec linii, mx/y wsp końca linii
    const mx = cx + (radius + offset + extraOffset) * cos;
    const my = cy + (radius + offset + extraOffset) * sin;
    return { sx, sy, mx, my, cos, sin };
}

// render linii
const renderLabelLine = (radius, defaultExtra = 0) => props => {
    const { payload } = props;
    // dodatkowy odstęp dla "Moja ilość" - w ramach polepszenia przejrzystości danych na wykresie
    const extra = defaultExtra + (payload.name.includes("Moja ilość") ? 20 : 0);
    const { sx, sy, mx, my } = computePosition({
        ...props,
        radius,
        extraOffset: extra
    });
    return <line x1={sx} y1={sy} x2={mx} y2={my} stroke={payload.fill} />;
};

// render etykiety
const renderLabelText = (radius, defaultExtra = 0) => props => {
    const { value, payload } = props;
    const extra = defaultExtra + (payload.name.includes("Moja ilość") ? 20 : 0);
    // obliczenie punktu początkowego etykiety
    const { mx, my, cos } = computePosition({
        ...props,
        radius,
        offset: 10,
        extraOffset: extra
    });
    const tx = mx + (cos > 0 ? 5 : -5);
    const ty = my;

    const display = payload.name === 'Pozostałe'
        ? `Pozostałe: ${value}`
        : value;
    return (
        <text
            x={tx}
            y={ty}
            fill={payload.fill}
            textAnchor={cos > 0 ? "start" : "end"}
            dominantBaseline="central"
            style={{ fontSize: 12 }}
        >
            {display}
        </text>
    );
};

export const TwoLevelPieChartAnaliza = ({
                                    title,
                                    innerData,
                                    innerKey,
                                    outerData = [],
                                    outerKey,
                                    colorsInner = [],
                                    colorsOuter = [],
                                    hideExelExport = false,
                                    hideCopyToclipboard = false,
                                    selectors,
                                    tooltipKey,

                                   }) => {

    const tooltipHtml = useSelector(state =>
        state.chartTooltips.msg[tooltipKey]?.value
    );

    // stałe promienie
    const innerRadius = 60;
    const outerRadius = 90;

    const totalOuter = outerData.reduce((sum, x) => sum + x[outerKey], 0);
    const threshold = totalOuter * 0.01; // 1% progu
    const smallOuter = outerData.filter(x => x[outerKey] < threshold);
    const largeOuter = outerData.filter(x => x[outerKey] >= threshold);

    const sumSmall = smallOuter.reduce((sum, x) => sum + x[outerKey], 0);
    const aggregated = {
        name: 'Pozostałe',
        [outerKey]: sumSmall
    };
    const mainOuterData = [...largeOuter, aggregated];

    const colorMoje = colorsOuter[0];
    const colorReszty = colorsOuter[1];
    const colorPozostale = '#df6506';
    const smallOuterColors = smallOuter.map(o =>
        o.name.startsWith('Moja ilość') ? colorMoje : colorReszty
    );
    const mainOuterDataColors = mainOuterData.map((o, idx) =>
        // jeśli to ostatni element w mainOuterData, czyli pozostałe:
        idx === mainOuterData.length - 1
            ? colorPozostale
            : (o.name.startsWith('Moja ilość') ? colorsOuter[0] : colorsOuter[1])
    );

    const exportData = innerData.map(inner => {
        const cycle = inner.name;
        const mineEntry = outerData.find(o =>
            o.name.startsWith('Moja ilość') && o.name.includes(cycle)
        );
        const restEntry = outerData.find(o =>
            o.name.startsWith('Reszta') && o.name.includes(cycle)
        );

        return {
            cykl: cycle,
            suma_ilosc_wszystkich_pracownikow: inner.value,
            ilosc_moje:                      mineEntry?.value  || 0,
            ilosc_reszty_zespolu:            restEntry?.value  || 0
        };
    });

    return (
        <Card>
            <Card.Header>
                <div className={'d-flex justify-content-between gap-3'}>
                    <div className={'d-flex justify-content-between'}>
                        <div className={'fs-5'}>
                            {title ?? 'Analiza'}
                        </div>
                        <div>
                            {tooltipHtml && (
                                <TooltipInfo text={tooltipHtml} className="ms-2" />
                            )}
                        </div>
                    </div>
                    <div>
                        {!!selectors && selectors()}
                    </div>
                    <div>
                        {(!hideExelExport && exportData) &&
                            <ExportToExcel
                                data={exportData}
                                fileName={title || 'analiza'}
                            />
                        }
                        {(!hideCopyToclipboard && exportData) &&
                            <CopyDataToClipboard data={exportData}/>
                        }
                    </div>
                </div>
            </Card.Header>
            <Card.Body>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    {/* GŁÓWNY WYKRES */}
                    <div style={{ flex: smallOuter.length ? 2 : 1 }}>
                        <h6>Wykres główny:</h6>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Tooltip />
                                {/* wewnętrzne koło */}
                                <Pie
                                    // startAngle={180}
                                    // endAngle={0}
                                    data={innerData}
                                    dataKey={innerKey}
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={innerRadius}
                                    label={false}
                                    labelLine={false}
                                >
                                    {innerData.map((_, idx) => (
                                        <Cell key={`in-${idx}`} fill={colorsInner[idx % colorsInner.length]} />
                                    ))}
                                </Pie>
                                {/* zewnętrzne koło */}
                                <Pie
                                    // startAngle={180}
                                    // endAngle={0}
                                    data={mainOuterData}
                                    dataKey={outerKey}
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={innerRadius + 5}
                                    outerRadius={outerRadius}
                                    label={renderLabelText(outerRadius, 20)}
                                    labelLine={renderLabelLine(outerRadius, 20)}
                                >
                                    {mainOuterData.map((_, idx) => (
                                        <Cell key={`out-${idx}`} fill={mainOuterDataColors[idx]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* DODATKOWY WYKRES „Pozostałe” */}
                    {smallOuter.length > 0 && (
                        <div style={{ flex: 1, textAlign: "center" }}>
                            <h6>Wykres prezentujący część "pozostałe":</h6>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Tooltip />
                                    <Pie
                                        // startAngle={180}
                                        // endAngle={0}
                                        data={smallOuter}
                                        dataKey={outerKey}
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label={renderLabelText(80, 20)}
                                        labelLine={renderLabelLine(80, 20)}
                                    >
                                        {smallOuter.map((_, idx) => (
                                            <Cell key={`small-${idx}`} fill={smallOuterColors[idx]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

TwoLevelPieChartAnaliza.propTypes = {
    title: PropTypes.string.isRequired,
    innerData: PropTypes.array.isRequired,
    innerKey: PropTypes.string.isRequired,
    outerData: PropTypes.array,
    outerKey: PropTypes.string,
    colorsInner: PropTypes.array,
    colorsOuter: PropTypes.array,
    //isOperator: PropTypes.bool,
    selectors: PropTypes.func,
    hideExelExport: PropTypes.bool,
    hideCopyToclipboard: PropTypes.bool,
    tooltipKey: PropTypes.string,


};